import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../../hooks/useLanguage';

interface QuoteAnalyticsProps {
    data: {
        total: {
            created: number;
            accepted: number;
            rejected: number;
            pending: number;
        };
        rates: {
            acceptance: number;
        };
        amounts: {
            average: number;
            total: number;
        };
        timeSeries: Array<{
            date: string;
            created: number;
            accepted: number;
            rejected: number;
            amount: number;
        }>;
    };
}

export const QuoteAnalytics: React.FC<QuoteAnalyticsProps> = ({ data }) => {
    const { t, isRTL, currentLanguage } = useLanguage();

    // Formater les données pour le graphique avec traductions
    const chartData = data.timeSeries.map(item => ({
        date: new Date(item.date).toLocaleDateString(
            currentLanguage === 'ar' ? 'ar-SA' :
                currentLanguage === 'en' ? 'en-GB' : 'fr-FR',
            { day: '2-digit', month: '2-digit' }
        ),
        [t('quoteAnalytics.chart.created')]: item.created,
        [t('quoteAnalytics.chart.accepted')]: item.accepted,
        [t('quoteAnalytics.chart.amount')]: item.amount
    }));

    return (
        <div className={`bg-white rounded-lg shadow p-6 ${isRTL ? 'rtl' : ''}`}>
            <h3 className="text-xl font-bold mb-6">
                {t('quoteAnalytics.title')}
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div>
                    <h4 className="text-sm text-gray-500">
                        {t('quoteAnalytics.total.created')}
                    </h4>
                    <p className="text-2xl font-bold">{data.total.created}</p>
                </div>
                <div>
                    <h4 className="text-sm text-gray-500">
                        {t('quoteAnalytics.total.accepted')}
                    </h4>
                    <p className="text-2xl font-bold">{data.total.accepted}</p>
                </div>
                <div>
                    <h4 className="text-sm text-gray-500">
                        {t('quoteAnalytics.total.pending')}
                    </h4>
                    <p className="text-2xl font-bold">{data.total.pending}</p>
                </div>
                <div>
                    <h4 className="text-sm text-gray-500">
                        {t('quoteAnalytics.total.rejected')}
                    </h4>
                    <p className="text-2xl font-bold">{data.total.rejected}</p>
                </div>
            </div>

            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            reversed={isRTL}
                        />
                        <YAxis
                            yAxisId="left"
                            orientation={isRTL ? "right" : "left"}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation={isRTL ? "left" : "right"}
                        />
                        <Tooltip />
                        <Legend />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={t('quoteAnalytics.chart.created')}
                            stroke="#8b5cf6"
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={t('quoteAnalytics.chart.accepted')}
                            stroke="#06b6d4"
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey={t('quoteAnalytics.chart.amount')}
                            stroke="#f59e0b"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-sm text-purple-800 font-medium">
                        {t('quoteAnalytics.rates.acceptance')}
                    </h4>
                    <p className="text-2xl font-bold text-purple-600">
                        {data.rates.acceptance.toFixed(1)}%
                    </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                    <h4 className="text-sm text-amber-800 font-medium">
                        {t('quoteAnalytics.rates.averageAmount')}
                    </h4>
                    <p className={`text-2xl font-bold text-amber-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {isRTL
                            ? `${data.amounts.average.toFixed(2)} ${t('quoteAnalytics.currency')} `
                            : `${data.amounts.average.toFixed(2)}${t('quoteAnalytics.currency')}`
                        }
                    </p>
                </div>
                <div className="bg-cyan-50 p-4 rounded-lg">
                    <h4 className="text-sm text-cyan-800 font-medium">
                        {t('quoteAnalytics.rates.totalAmount')}
                    </h4>
                    <p className={`text-2xl font-bold text-cyan-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {isRTL
                            ? `${data.amounts.average.toFixed(2)} ${t('quoteAnalytics.currency')}`
                            : `${data.amounts.total.toFixed(2)}${t('quoteAnalytics.currency')}`
                        }
                    </p>
                </div>
            </div>
        </div>
    );
};